import { Text } from '../../primitives/Text';
import { cn } from '../../utils/cn';
import { TAB_ICON_SIZE } from './Tab.icons';
import type { TabItem } from './Tab.types';
import { tabItemVariants, tabLabelVariants } from './Tab.variants';

/** Derived render flags for one tab item. */
export interface TabItemState {
  isActive: boolean;
  disabled: boolean;
}

export function tabItemStateOf<V extends string>(
  item: TabItem<V>,
  value: V,
  tabDisabled: boolean,
): TabItemState {
  return {
    isActive: item.value === value,
    disabled: tabDisabled || !!item.disabled,
  };
}

/** Full className for one item's content row. */
export function tabItemClassName(state: TabItemState, extra?: string): string {
  return cn(tabItemVariants({ active: state.isActive, disabled: state.disabled }), extra);
}

function isTextLike(c: unknown): boolean {
  if (c == null || typeof c === 'boolean') return false;
  if (typeof c === 'string' || typeof c === 'number') return true;
  if (Array.isArray(c)) return c.every(isTextLike);
  return false;
}

/**
 * The content of one item's row — leading 20px icon and/or label (Figma
 * `Type` = Icon / Label / Icon+Label). Rendered as a fragment: the pressable
 * row itself owns the layout (`flex-row items-center gap-2`).
 *
 * `iconColor` is `currentColor` on web (inherits the item's text colour) and
 * a resolved hex on native (`resolveTabIconColor`).
 */
export function TabItemContent<V extends string>({
  item,
  state,
  iconColor,
}: {
  item: TabItem<V>;
  state: TabItemState;
  iconColor: string;
}) {
  const Icon = item.icon;
  const label =
    item.label != null && isTextLike(item.label) ? (
      <Text
        className={tabLabelVariants({
          active: state.isActive,
          disabled: state.disabled,
        })}>
        {item.label}
      </Text>
    ) : (
      item.label
    );

  return (
    <>
      {Icon ? <Icon color={iconColor} size={TAB_ICON_SIZE} /> : null}
      {label}
    </>
  );
}
